import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send, MessageSquare, HelpCircle, Briefcase, CheckCircle } from 'lucide-react';
import { contactAPI } from '../services/api';

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', type: 'general' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      await contactAPI.submit(form);
      setSubmitted(true);
    } catch (err) {
      setError('Sorry, we could not send your message just now. Please email hello@ariv.co.uk directly.');
    } finally {
      setSending(false);
    }
  };

  const offices = [
    {
      city: 'London (HQ)',
      address: 'Bowness House, 815 Old Kent Rd, London SE15 1NX',
      phone: '+44 20 7946 0200',
      email: 'hello@ariv.co.uk',
      hours: 'Mon–Fri 9am–6pm GMT',
    },
  ];

  const contactTypes = [
    { value: 'general', label: 'General Enquiry', icon: <MessageSquare size={16} /> },
    { value: 'support', label: 'Host Support', icon: <HelpCircle size={16} /> },
    { value: 'sales', label: 'Enterprise Sales', icon: <Briefcase size={16} /> },
    { value: 'press', label: 'Press & Media', icon: <Mail size={16} /> },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><div className="h-[30px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></div></Link>
          <div className="flex items-center gap-6">
            <Link to="/about" className="text-gray-600 hover:text-[#1F3A68] text-sm font-medium">About</Link>
            <Link to="/help" className="text-gray-600 hover:text-[#1F3A68] text-sm font-medium">Help Center</Link>
            <Link to="/login" className="bg-[#1F3A68] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1F3A68]/90">Sign In</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-[#1F3A68] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-5xl font-bold mb-4">Get in touch</h1>
          <p className="text-white/80 text-xl max-w-xl">
            Whether you're a host, landlord, or just curious about what we do — we'd love to hear from you.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Form */}
          <div className="lg:col-span-2">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle size={40} className="text-green-500" />
                </div>
                <h2 className="text-3xl font-bold text-[#1F3A68] mb-3">Message sent!</h2>
                <p className="text-gray-600 text-lg mb-8">
                  Thanks for reaching out. We typically respond within 1–2 business days.
                </p>
                <button
                  onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', message: '', type: 'general' }); }}
                  className="text-[#1F3A68] font-semibold hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <h2 className="text-2xl font-bold text-[#1F3A68] mb-6">Send us a message</h2>

                {/* Contact type selector */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">I'm enquiring about…</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {contactTypes.map(ct => (
                      <button
                        key={ct.value}
                        type="button"
                        onClick={() => setForm(f => ({ ...f, type: ct.value }))}
                        className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                          form.type === ct.value
                            ? 'border-[#1F3A68] bg-[#1F3A68] text-white'
                            : 'border-gray-200 text-gray-600 hover:border-[#1F3A68]/40'
                        }`}
                      >
                        {ct.icon}{ct.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full name *</label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Jane Smith"
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email address *</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      placeholder="jane@example.com"
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Subject *</label>
                  <input
                    type="text"
                    required
                    value={form.subject}
                    onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                    placeholder="How can we help?"
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Message *</label>
                  <textarea
                    required
                    rows={6}
                    value={form.message}
                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                    placeholder="Tell us more about your enquiry…"
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68] resize-none"
                  />
                </div>

                {error && (
                  <p className="text-sm text-[#CB3B3B] font-medium">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={sending}
                  className="flex items-center gap-2 bg-[#1F3A68] text-white px-8 py-4 rounded-xl font-bold hover:bg-[#1F3A68]/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Send size={18} />
                  {sending ? 'Sending…' : 'Send Message'}
                </button>
              </form>
            )}
          </div>

          {/* Sidebar info */}
          <div className="space-y-6">
            {/* Response time */}
            <div className="bg-[#1F3A68]/5 border border-[#1F3A68]/10 rounded-2xl p-6">
              <div className="flex items-center gap-2 text-[#1F3A68] font-bold mb-3">
                <Clock size={18} />
                Response times
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">General enquiries</span>
                  <span className="font-semibold text-gray-800">1–2 business days</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Host support</span>
                  <span className="font-semibold text-green-600">Within 4 hours</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Enterprise sales</span>
                  <span className="font-semibold text-gray-800">Same day</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Press</span>
                  <span className="font-semibold text-gray-800">1 business day</span>
                </div>
              </div>
            </div>

            {/* Offices */}
            <div>
              <h3 className="text-lg font-bold text-[#1F3A68] mb-4">Our offices</h3>
              {offices.map((office, i) => (
                <div key={i} className="border border-gray-100 rounded-2xl p-5 mb-4 hover:shadow-sm transition-shadow">
                  <h4 className="font-bold text-[#1F3A68] mb-3">{office.city}</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-2 text-gray-600">
                      <MapPin size={14} className="mt-0.5 flex-shrink-0 text-[#CB3B3B]" />
                      {office.address}
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone size={14} className="flex-shrink-0 text-[#CB3B3B]" />
                      {office.phone}
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail size={14} className="flex-shrink-0 text-[#CB3B3B]" />
                      {office.email}
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock size={14} className="flex-shrink-0 text-[#CB3B3B]" />
                      {office.hours}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick links */}
            <div className="bg-gray-50 rounded-2xl p-5">
              <h4 className="font-bold text-[#1F3A68] mb-3">Quick links</h4>
              <div className="space-y-2">
                <Link to="/help" className="block text-sm text-[#1F3A68] hover:underline">→ Help Center & FAQs</Link>
                <Link to="/trust" className="block text-sm text-[#1F3A68] hover:underline">→ Trust & Safety</Link>
                <Link to="/careers" className="block text-sm text-[#1F3A68] hover:underline">→ Join our team</Link>
                <Link to="/become-host" className="block text-sm text-[#1F3A68] hover:underline">→ Become a host</Link>
                <Link to="/terms" className="block text-sm text-[#1F3A68] hover:underline">→ Terms & Privacy</Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1F3A68] text-white py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="h-[23px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[23px] w-auto object-contain brightness-0 invert" /></div>
          <div className="flex gap-6 text-sm text-white/70">
            <Link to="/about" className="hover:text-white">About</Link>
            <Link to="/careers" className="hover:text-white">Careers</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
            <Link to="/trust" className="hover:text-white">Trust &amp; Safety</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
          </div>
          <p className="text-white/50 text-sm">&copy; {new Date().getFullYear()} Ariv. A product of Brick & Capital Ltd.</p>
          <p className="text-white/40 text-xs mt-2"><strong>Brick &amp; Capital Ltd</strong> &middot; Registered in England and Wales, company number <a href="https://find-and-update.company-information.service.gov.uk/company/16655788" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/70">16655788</a> &middot; Registered office: Bowness House, 815 Old Kent Road, London SE15 1NX</p>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
